import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { Locale, Messages } from '@repere/core';
import { Alerte, Bouton, Champ, ChampDate, Invisible, Picto, Vide } from '../ui';
import { ECRAN_LARGE, useRequeteMedia } from '../ui/media';
import { messageDErreur, useSession } from '../auth/session';
import { estLisibleParIA, reduirePourLecture } from './reduireImage';
import { dateComplete } from './dates';
import { useTraduction } from '../i18n/langue';
import type { ParentDuDossier } from './types';

type VoletDepenses = 'declarer' | 'detail' | 'partagees';

type StatutDepense = 'declared' | 'accepted' | 'disputed' | 'settled';

interface Depense {
  id: string;
  amountCents: number;
  currency: string;
  category: string;
  description: string | null;
  spentOn: string;
  status: StatutDepense;
  payerId: string | null;
  payerSlot: number | null;
  replacesExpenseId: string | null;
  replacedByExpenseId: string | null;
  /** Le justificatif joint, s'il y en a un — un document du dossier. */
  receiptDocumentId: string | null;
}

interface LigneSolde {
  expenseId: string;
  spentOn: string;
  amountCents: number;
  category: string | null;
  payerSlot: number;
  owedByOtherCents: number;
}

interface Solde {
  debtorSlot: number | null;
  creditorSlot: number | null;
  amountCents: number;
  lines: LigneSolde[];
  sansRegle: string[];
}

/**
 * Libellés des statuts.
 *
 * Aucun n'attribue une action à une personne : « Contestée », jamais « Refusée par
 * Dominique ». La formulation factuelle est une contrainte de produit — nommer l'auteur
 * d'un désaccord transforme l'historique en dossier à charge (docs/01 §5.5).
 */
function libelleStatut(m: Messages['dossier']['depenses']): Record<StatutDepense, string> {
  return {
    declared: m.statutAttente,
    accepted: m.statutAcceptee,
    disputed: m.statutContestee,
    settled: m.statutRemboursee,
  };
}

/** Les statuts empruntent les pastilles des demandes d'accord : même langage, même écran. */
const CLASSE_STATUT: Record<StatutDepense, string> = {
  declared: 'attente',
  accepted: 'acceptee',
  disputed: 'declinee',
  settled: 'acceptee',
};

/** Ce que l'API rend d'une lecture de justificatif : trois propositions, ou un motif. */
interface Lecture {
  montantCents?: number;
  date?: string;
  categorie?: string;
  /** Ce que dit le ticket, en une phrase : commerce et nature de l'achat. */
  details?: string;
  motif?: 'refuse' | 'indisponible' | 'budget' | 'quota' | 'type' | 'illisible';
}

/**
 * Les six catégories, dans leur forme **canonique** — celle qui part au serveur et qui
 * y est déjà stockée pour toute dépense passée.
 *
 * Ce n'est pas ce qui s'affiche : `messages.dossier.depenses.categories` porte la même
 * liste, traduite, dans le même ordre. Les deux doivent rester alignées position à
 * position — c'est ce que vérifie le test de ce fichier. Confondre les deux ferait
 * dépendre la catégorie **stockée** de la langue de qui déclare la dépense, et deux
 * parents qui lisent le même dossier dans deux langues verraient alors deux catégories
 * différentes pour la même dépense.
 */
const CATEGORIES_VALEURS = [
  'santé',
  'scolarité',
  'cantine',
  'activités',
  'vêtements',
  'transport',
] as const;

/** Le libellé affiché d'une catégorie déjà enregistrée, quelle que soit la langue courante. */
function libelleCategorie(valeur: string, m: Messages['dossier']['depenses']): string {
  const index = CATEGORIES_VALEURS.indexOf(valeur as (typeof CATEGORIES_VALEURS)[number]);
  return index === -1 ? valeur : (m.categories[index] ?? valeur);
}

/** Montant en centimes → « 143,50 € ». L'affichage arrondit ; le calcul, jamais. */
export function euros(centimes: number, locale: Locale = 'fr'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(
    centimes / 100,
  );
}

/** « 143,50 » saisi par un parent → 14350 centimes. Refuse ce qui n'est pas un montant. */
export function enCentimes(saisie: string): number | null {
  const normalise = saisie.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(normalise)) return null;
  // On passe par une chaîne de centimes plutôt que par une multiplication flottante :
  // `19.99 * 100` vaut 1998.9999999999998, et l'arrondi finirait par mordre.
  const [entiers, decimales = ''] = normalise.split('.');
  return Number(`${entiers}${decimales.padEnd(2, '0')}`);
}

/**
 * L'appareil photo, en silhouette pleine — le boîtier, la bosse du flash, l'objectif
 * creusé en négatif. Même langage que `Picto` (`ui/Picto.tsx`), en une seule teinte
 * (`currentColor`) : ce n'est pas une catégorie du dossier, juste un geste.
 */
function IconeAppareilPhoto({ taille = 20 }: { taille?: number }) {
  return (
    <svg width={taille} height={taille} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fillRule="evenodd"
        fill="currentColor"
        d="M4 7 Q4 5 6 5 L9 5 L10 3.6 Q10.4 3 11.2 3 L12.8 3 Q13.6 3 14 3.6 L15 5 L18 5 Q20 5 20 7 L20 17 Q20 19 18 19 L6 19 Q4 19 4 17 Z M8.6 12.6 A3.4 3.4 0 1 0 15.4 12.6 A3.4 3.4 0 1 0 8.6 12.6 Z"
      />
    </svg>
  );
}

/**
 * Dépenses partagées du dossier.
 *
 * Trois sections : le solde (« Détail »), la saisie (« Déclarer une dépense ») et
 * l'historique (« Dépenses partagées »). Sur le rail, elles s'empilent dans une carte
 * unique — la hauteur ne manque pas. **Sur téléphone**, chacune tenait dans son propre
 * encadré, imbriqué dans celui de l'écran : trois bordures avant d'arriver au premier
 * chiffre. Elles deviennent trois volets, comme le Calendrier — un geste remplace un
 * défilement, et chaque volet occupe l'écran plutôt qu'une fenêtre bornée au milieu.
 */
export function Depenses({
  caseFileId,
  parents,
  monUserId,
}: {
  caseFileId: string;
  parents: ParentDuDossier[];
  monUserId: string | null;
}) {
  const { client, profil, rafraichirProfil } = useSession();
  const { locale, messages } = useTraduction();
  const m = messages.dossier.depenses;
  const statuts = libelleStatut(m);
  const queryClient = useQueryClient();
  const rail = useRequeteMedia(ECRAN_LARGE);
  const [volet, setVolet] = useState<VoletDepenses>('declarer');
  const [montant, setMontant] = useState('');
  // Aucune catégorie par défaut : le menu partait sur « cantine », ce qui choisissait à
  // la place du parent — et une dépense mal rangée fausse la répartition sans le dire.
  const [categorie, setCategorie] = useState<string>('');
  /**
   * Ce que dit le ticket, en une phrase.
   *
   * Le champ existait dans l'API depuis les dépenses (`description`) et n'avait jamais eu
   * d'endroit où s'écrire : un montant et une catégorie ne disent pas ce qui a été acheté,
   * et c'est justement la question que pose l'autre parent quand une dépense surprend.
   */
  const [commentaire, setCommentaire] = useState('');
  const [justificatif, setJustificatif] = useState<{ id: string; nom: string } | null>(null);
  const champFichier = useRef<HTMLInputElement>(null);
  const champPhoto = useRef<HTMLInputElement>(null);
  const [date, setDate] = useState('');
  const [motif, setMotif] = useState<Record<string, string>>({});
  /**
   * Le fichier en attente d'un accord, quand la question n'a jamais été posée.
   *
   * Il est **gardé de côté, pas envoyé** : le justificatif rejoint bien le dossier tout de
   * suite, mais sa lecture attend la réponse. Sans cette attente, il faudrait redemander la
   * photo après le consentement, sur un écran où l'on vient déjà de la prendre.
   */
  const [aLire, setALire] = useState<File | null>(null);
  const accordLecture = profil?.receiptScanConsent;

  const depenses = useQuery({
    queryKey: ['expenses', caseFileId],
    queryFn: () => client.request<Depense[]>(`/case-files/${caseFileId}/expenses`),
  });

  const solde = useQuery({
    queryKey: ['balance', caseFileId],
    queryFn: () => client.request<Solde>(`/case-files/${caseFileId}/balance`),
  });

  const rafraichir = () => {
    void queryClient.invalidateQueries({ queryKey: ['expenses', caseFileId] });
    void queryClient.invalidateQueries({ queryKey: ['balance', caseFileId] });
  };

  const declarer = useMutation({
    mutationFn: () => {
      const centimes = enCentimes(montant);
      if (centimes === null) throw new Error('Montant invalide.');
      return client.request(`/case-files/${caseFileId}/expenses`, {
        method: 'POST',
        body: {
          amountCents: centimes,
          category: categorie,
          spentOn: date,
          // Vide, le champ est **omis** plutôt qu'envoyé vide : une chaîne vide serait un
          // commentaire, et l'API la refuserait (`Length(1, 500)`).
          ...(commentaire.trim() ? { description: commentaire.trim() } : {}),
          ...(justificatif ? { receiptDocumentId: justificatif.id } : {}),
        },
      });
    },
    onSuccess: () => {
      setMontant('');
      setDate('');
      setCategorie('');
      setCommentaire('');
      setJustificatif(null);
      rafraichir();
    },
  });

  /**
   * Le justificatif part **avant** la dépense, comme la pièce jointe d'un message.
   *
   * Analysé, chiffré et enregistré comme document du dossier, il est accepté ou refusé
   * pendant que rien n'est encore entré dans les comptes. Une dépense enregistrée puis
   * amputée de son ticket serait inexplicable — et le ticket est précisément ce qui
   * tranche si elle est contestée.
   */
  const deposer = useMutation({
    mutationFn: async (fichier: File) => {
      const formulaire = new FormData();
      formulaire.append('file', fichier);
      formulaire.append('category', 'justificatif');
      const document = await client.request<{ id: string; fileName: string }>(
        `/case-files/${caseFileId}/documents`,
        { method: 'POST', form: formulaire },
      );
      return { document: { id: document.id, nom: document.fileName }, fichier };
    },
    onSuccess: ({ document, fichier }) => {
      setJustificatif(document);
      // Le dépôt et la lecture sont deux gestes distincts, et le premier n'attend pas le
      // second : la pièce est au dossier même si la lecture échoue, est refusée, ou n'a
      // jamais été proposée. Un PDF, que le modèle ne voit pas, s'arrête ici.
      if (!estLisibleParIA(fichier)) return;
      if (accordLecture === true) lire.mutate(fichier);
      else if (accordLecture === null || accordLecture === undefined) setALire(fichier);
    },
  });

  /**
   * Demande la lecture du justificatif — montant, date et catégorie **proposés**.
   *
   * L'image est réduite dans le navigateur avant de partir : le coût d'une lecture est
   * proportionnel à la surface, et une photo de téléphone en fait seize fois trop. Les
   * valeurs lues remplissent les champs vides uniquement — ce que le parent a déjà saisi
   * fait foi, une lecture n'écrase jamais une frappe.
   */
  const lire = useMutation({
    mutationFn: async (fichier: File): Promise<Lecture> => {
      const reduite = await reduirePourLecture(fichier);
      // La réduction a échoué : format que le navigateur ne décode pas, canvas
      // indisponible. On ne dépense rien et le formulaire se saisit à la main.
      if (!reduite) return { motif: 'type' };
      const formulaire = new FormData();
      formulaire.append('file', reduite, 'justificatif.jpg');
      return client.request<Lecture>(`/case-files/${caseFileId}/expenses/read-receipt`, {
        method: 'POST',
        form: formulaire,
      });
    },
    onSuccess: (lu) => {
      if (lu.montantCents !== undefined && montant.trim() === '') {
        setMontant((lu.montantCents / 100).toFixed(2).replace('.', ','));
      }
      if (lu.date !== undefined && date === '') setDate(lu.date);
      if (lu.categorie !== undefined && categorie === '') setCategorie(lu.categorie);
      if (lu.details !== undefined && commentaire.trim() === '') setCommentaire(lu.details);
    },
  });

  /** Accorde — ou refuse — la lecture, et enchaîne si le parent vient de dire oui. */
  const repondreAccord = useMutation({
    mutationFn: (accord: boolean) =>
      client.request('/auth/receipt-scan-consent', { method: 'PATCH', body: { consent: accord } }),
    onSuccess: async (_, accord) => {
      await rafraichirProfil();
      const enAttente = aLire;
      setALire(null);
      if (accord && enAttente) lire.mutate(enAttente);
    },
  });

  const repondre = useMutation({
    mutationFn: ({ id, reponse }: { id: string; reponse: 'accepted' | 'disputed' }) =>
      client.request(`/expenses/${id}/response`, {
        method: 'POST',
        body: { response: reponse, ...(reponse === 'disputed' ? { reason: motif[id] ?? '' } : {}) },
      }),
    onSuccess: rafraichir,
  });

  const nomDuParent = (slot: number | null): string => {
    const parent = parents.find((p) => p.slot === slot);
    if (!parent) return m.unParent;
    return parent.isViewer ? m.vous : parent.displayName;
  };

  const visibles = (depenses.data ?? []).filter((d) => d.replacedByExpenseId === null);
  const centimesSaisis = enCentimes(montant);

  /**
   * Les trois volets, écrits une fois — empilés sur le rail, répartis en onglets sur
   * téléphone. C'est la **disposition** qui change, jamais le contenu (même principe que
   * les volets d'« Aujourd'hui » et du Calendrier dans `Dossier.tsx`).
   */
  const blocDetail = solde.data ? (
    <div className="solde">
      <p className="solde-phrase">
        {solde.data.amountCents === 0
          ? m.comptesAJour
          : m.soldePhrase(
              nomDuParent(solde.data.debtorSlot),
              parents.find((p) => p.slot === solde.data?.debtorSlot)?.isViewer ?? false,
              euros(solde.data.amountCents, locale),
              nomDuParent(solde.data.creditorSlot),
            )}
      </p>

      {/* Le détail reste à un geste : un solde qu'on ne peut pas refaire ne vaut rien
          sur un écran que deux parents relisent. Il n'a simplement plus à occuper la
          hauteur d'une carte en permanence. */}
      <details className="solde-detail">
        <summary>{m.detailLigneALigne}</summary>
        <ul>
          {solde.data.lines.map((ligne) => (
            <li key={ligne.expenseId}>
              {m.ligneDetail(
                dateComplete(new Date(ligne.spentOn), locale),
                ligne.category ?? '',
                euros(ligne.amountCents, locale),
                nomDuParent(ligne.payerSlot),
                euros(ligne.owedByOtherCents, locale),
              )}
            </li>
          ))}
        </ul>
        <p className="discret">{m.soldeNonStocke}</p>
      </details>

      {solde.data.sansRegle.length > 0 ? (
        <Alerte titre={m.sansRegleTitre}>{m.sansRegleTexte(solde.data.sansRegle.length)}</Alerte>
      ) : null}
    </div>
  ) : null;

  const blocDeclarer = (
    <form
      className="declarer"
      onSubmit={(e) => {
        e.preventDefault();
        declarer.mutate();
      }}
    >
      {/* Sur le rail, ce titre distingue la saisie du solde et de l'historique dans une
          carte unique. Sur téléphone, l'onglet le nomme déjà — un `<h3>` identique juste
          en dessous ferait entendre deux fois la même chose au lecteur d'écran. */}
      {rail ? <h3>{m.declarerUneDepense}</h3> : null}

      {/* Le ticket ouvre la saisie : on a le papier en main au moment où l'on paie. */}
      {justificatif ? (
        <div className="justificatif">
          <Picto categorie="journal" taille={24} />
          <span className="justificatif-nom">
            <strong>{justificatif.nom}</strong>
            <span className="discret">{m.justificatifAnalyse}</span>
          </span>
          <Bouton variante="secondaire" onClick={() => setJustificatif(null)}>
            {m.retirer}
          </Bouton>
        </div>
      ) : (
        <div className="justificatif-vide">
          {/* `capture` ouvre l'appareil photo sur un téléphone et est ignoré ailleurs :
                le même bouton sert donc les deux, sans détection de plateforme.

                Masqués par une classe qui les sort visuellement de l'écran plutôt que par
                `hidden` (`display: none`) : dans la coque Capacitor comme sur Safari, un
                clic programmatique sur un champ `display: none` n'ouvre pas l'appareil
                photo — le geste doit rester possible pour le moteur, même invisible pour
                l'œil. `tabIndex={-1}` et `aria-hidden` les retirent du parcours clavier et
                du lecteur d'écran, qui n'ont affaire qu'au bouton. */}
          <input
            ref={champPhoto}
            type="file"
            accept="image/*"
            capture="environment"
            className="invisible"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              const fichier = e.target.files?.[0];
              if (fichier) deposer.mutate(fichier);
            }}
          />
          <input
            ref={champFichier}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
            className="invisible"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              const fichier = e.target.files?.[0];
              if (fichier) deposer.mutate(fichier);
            }}
          />

          {/* Une ligne : l'appareil photo est le geste attendu, un pictogramme suffit à
              l'annoncer. « Choisir un fichier » reste à côté, en lien, pour le PDF ou la
              photo déjà prise — un second bouton pleine largeur lui aurait donné le même
              poids qu'à un geste qu'on ne fait presque jamais. */}
          <div className="capture-ticket">
            <Bouton
              className="capture-bouton"
              onClick={() => champPhoto.current?.click()}
              disabled={deposer.isPending}
            >
              <IconeAppareilPhoto taille={18} />
              {deposer.isPending ? m.envoiEnCours : m.photographierLeTicket}
            </Bouton>
            <Bouton
              variante="lien"
              onClick={() => champFichier.current?.click()}
              disabled={deposer.isPending}
            >
              {m.choisirUnFichier}
            </Bouton>
          </div>
          <p className="aide">{m.justificatifAide}</p>
        </div>
      )}

      {deposer.isError ? (
        <Alerte titre={m.erreurJustificatif}>{messageDErreur(deposer.error)}</Alerte>
      ) : null}

      {/* La question, posée **une fois**, au premier justificatif déposé — pas à
            l'inscription, où elle n'aurait aucun contexte. Elle dit ce qui part, où, et
            que rien n'est conservé : c'est la seule donnée du dossier qui sorte de nos
            serveurs, et un consentement qui ne nomme pas ce qu'il couvre n'en est pas un. */}
      {aLire ? (
        <div className="lecture-accord">
          <p className="lecture-titre">{m.lectureTitre}</p>
          <p>{m.lectureQuestion}</p>
          <p className="discret">{m.lectureDetail}</p>
          <p style={{ marginBottom: 0 }}>
            <Bouton onClick={() => repondreAccord.mutate(true)} disabled={repondreAccord.isPending}>
              {m.lectureAccepter}
            </Bouton>{' '}
            <Bouton
              variante="secondaire"
              onClick={() => repondreAccord.mutate(false)}
              disabled={repondreAccord.isPending}
            >
              {m.lectureRefuser}
            </Bouton>
          </p>
        </div>
      ) : null}

      {lire.isPending ? (
        <p className="lecture-scan" role="status">
          <span className="lecture-scan-viseur">
            <IconeAppareilPhoto taille={16} />
            <span className="lecture-scan-onde" aria-hidden="true" />
          </span>
          {m.lectureEnCours}
        </p>
      ) : null}

      {lire.isSuccess ? (
        <p className="discret">
          {lire.data.motif === 'quota'
            ? m.lectureQuota
            : lire.data.motif === 'budget'
              ? m.lectureBudget
              : lire.data.motif === 'illisible'
                ? m.lectureRien
                : lire.data.motif
                  ? m.lectureIndisponible
                  : m.lectureProposee}
        </p>
      ) : null}

      {lire.isError ? <p className="discret">{m.lectureIndisponible}</p> : null}

      <div className="declarer-chiffres">
        <Champ
          label={m.montant}
          value={montant}
          onChange={(e) => setMontant(e.target.value)}
          inputMode="decimal"
          placeholder="24,90"
          {...(montant && centimesSaisis === null ? { erreur: m.montantInvalide } : {})}
        />
        <ChampDate
          label={m.dateDeLaDepense}
          value={date}
          onChange={setDate}
          fenetre={{ avant: 2, apres: 0 }}
          traduction={messages.dates}
        />
      </div>

      {/* Six catégories fixes : des pastilles plutôt qu'un menu, qui obligeait à ouvrir
            pour voir ses valeurs et partait pré-réglé sur l'une d'elles. */}
      <div className="filtres" role="group" aria-label={m.ariaCategorie}>
        {CATEGORIES_VALEURS.map((valeur, index) => (
          <button
            key={valeur}
            type="button"
            className={valeur === categorie ? 'filtre-onglet actif' : 'filtre-onglet'}
            aria-pressed={valeur === categorie}
            onClick={() => setCategorie(valeur)}
          >
            {m.categories[index]}
          </button>
        ))}
      </div>

      {/* Le commentaire vient après la catégorie : on dit d'abord combien et de quoi il
            s'agit, puis on précise. Deux lignes, pas dix — ce qu'on veut lire sous un
            montant tient en une phrase, et la pièce jointe porte le reste. */}
      <div className="champ">
        <label htmlFor="depense-commentaire">{m.commentaire}</label>
        <textarea
          id="depense-commentaire"
          rows={2}
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
          maxLength={500}
          placeholder={m.commentairePlaceholder}
          aria-describedby="depense-commentaire-aide"
        />
        <p className="aide" id="depense-commentaire-aide">
          {m.commentaireAide}
        </p>
      </div>

      {declarer.isError ? (
        <Alerte titre={m.erreurDeclaration}>{messageDErreur(declarer.error)}</Alerte>
      ) : null}

      <div className="declarer-ligne">
        <Bouton
          type="submit"
          disabled={centimesSaisis === null || !date || !categorie || declarer.isPending}
        >
          {declarer.isPending ? m.enregistrementEnCours : m.declarerLaDepense}
        </Bouton>
        <span className="discret">{categorie ? m.aideAvecCategorie : m.aideSansCategorie}</span>
      </div>
    </form>
  );

  const blocPartagees = (
    <>
      {depenses.isError ? (
        <Alerte titre={m.erreurChargement}>{messageDErreur(depenses.error)}</Alerte>
      ) : null}

      <ul className="liste-depenses">
        {visibles.map((depense) => {
          const aMoi = depense.payerId === monUserId;
          return (
            <li key={depense.id} className="depense">
              <div className="depense-ligne">
                {depense.receiptDocumentId ? (
                  <span className="depense-piece" title={m.justificatifJoint}>
                    <Picto categorie="journal" taille={20} />
                    <Invisible>{m.justificatifJoint}</Invisible>
                  </span>
                ) : null}
                <span className="depense-corps">
                  <span className="entete">
                    <strong>{euros(depense.amountCents, locale)}</strong> ·{' '}
                    {libelleCategorie(depense.category, m)}
                  </span>
                  <span className="discret">
                    {m.avancePar(
                      dateComplete(new Date(depense.spentOn), locale),
                      nomDuParent(depense.payerSlot),
                    )}
                  </span>
                  {/* Le commentaire sous la ligne, et non dans une infobulle : il répond à
                      « c'était quoi ? », la question qui précède une contestation. */}
                  {depense.description ? (
                    <span className="depense-commentaire">{depense.description}</span>
                  ) : null}
                </span>
                <span className={`statut-accord ${CLASSE_STATUT[depense.status]}`}>
                  {statuts[depense.status]}
                </span>
              </div>

              {depense.status === 'declared' && !aMoi ? (
                <div className="reponse-depense">
                  <Bouton onClick={() => repondre.mutate({ id: depense.id, reponse: 'accepted' })}>
                    {m.accepter}
                  </Bouton>
                  <Champ
                    label={m.motifSiConteste}
                    value={motif[depense.id] ?? ''}
                    onChange={(e) =>
                      setMotif((precedent) => ({ ...precedent, [depense.id]: e.target.value }))
                    }
                    aide={m.motifAide}
                  />
                  <Bouton
                    variante="secondaire"
                    disabled={!(motif[depense.id] ?? '').trim()}
                    onClick={() => repondre.mutate({ id: depense.id, reponse: 'disputed' })}
                  >
                    {m.contester}
                  </Bouton>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {visibles.length === 0 && !depenses.isPending ? (
        <Vide categorie="depenses">{m.videListe}</Vide>
      ) : null}
    </>
  );

  if (rail) {
    return (
      <>
        {blocDetail}
        {blocDeclarer}
        {blocPartagees}
      </>
    );
  }

  return (
    <>
      {/* Même dessin que les volets du Calendrier : les libellés sont longs, ils reviennent
          à la ligne plutôt que de se faire tronquer. */}
      <div
        className="filtres volets-depenses"
        role="tablist"
        aria-label={messages.dossier.categories.depenses}
      >
        <button
          type="button"
          role="tab"
          id="onglet-depenses-declarer"
          aria-selected={volet === 'declarer'}
          aria-controls="volet-depenses-declarer"
          className={volet === 'declarer' ? 'filtre-onglet actif' : 'filtre-onglet'}
          onClick={() => setVolet('declarer')}
        >
          {m.declarerUneDepense}
        </button>
        <button
          type="button"
          role="tab"
          id="onglet-depenses-detail"
          aria-selected={volet === 'detail'}
          aria-controls="volet-depenses-detail"
          className={volet === 'detail' ? 'filtre-onglet actif' : 'filtre-onglet'}
          onClick={() => setVolet('detail')}
        >
          {m.ongletDetail}
        </button>
        <button
          type="button"
          role="tab"
          id="onglet-depenses-partagees"
          aria-selected={volet === 'partagees'}
          aria-controls="volet-depenses-partagees"
          className={volet === 'partagees' ? 'filtre-onglet actif' : 'filtre-onglet'}
          onClick={() => setVolet('partagees')}
        >
          {m.ongletPartagees}
        </button>
      </div>

      {/* Chaque volet est une carte, comme ceux d'« Aujourd'hui » et du Calendrier : la
          rangée d'onglets promet un panneau qu'on change, et sans bord rien ne disait où il
          s'arrêtait. Une seule carte par volet, et non une par section : c'est l'empilement
          d'encadrés — le solde dans la saisie dans l'écran — qui avait fait les retirer. */}
      {volet === 'declarer' ? (
        <div
          role="tabpanel"
          id="volet-depenses-declarer"
          aria-labelledby="onglet-depenses-declarer"
          className="carte volet-carte"
        >
          {blocDeclarer}
        </div>
      ) : null}

      {volet === 'detail' ? (
        <div
          role="tabpanel"
          id="volet-depenses-detail"
          aria-labelledby="onglet-depenses-detail"
          className="carte volet-carte"
        >
          {blocDetail}
        </div>
      ) : null}

      {volet === 'partagees' ? (
        <div
          role="tabpanel"
          id="volet-depenses-partagees"
          aria-labelledby="onglet-depenses-partagees"
          className="carte volet-carte"
        >
          {blocPartagees}
        </div>
      ) : null}
    </>
  );
}
